import React from 'react'
import { Form, Input, Select, Radio, Icon, Tooltip } from 'antd'
import { textInput, selectInput, columnTitle, addIcon } from '../../EditSubStoDataPopup/edit-sub-sto.scss'
import TextArea from 'antd/es/input/TextArea'
import { israelRetailBanks, cardNetworks, luhnCheck } from '@utils/index'
import moment from 'moment'
import { withFormContext, FormContextValue } from '../FormContext'

const { Item: FormItem } = Form

// ── constants ────────────────────────────────────────────────
const currentYear = moment().year()
const currentMonth = moment().month() + 1

const expireMonthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`
}))

const expireYearOptions = Array.from({ length: 16 }, (_, i) => ({
    value: currentYear + i,
    label: `${currentYear + i}`
}))

const debitTypeOptions = [
    { value: 'immediate_and_recurring', label: 'חיוב מיידי+הו״ק' },
    { value: 'recurring_only', label: 'הו״ק בלבד' }
]

const firstMonthOptions = [
    { value: 'setup_fee', label: 'דמי הקמה (18₪)' },
    { value: 'clearing_fee', label: 'דמי סליקה (3₪)' },
    { value: 'none', label: 'ללא דמי חיוב נוספים' }
]

const recurringChargeOptions = [
    { value: 'clearing_fee', label: 'דמי סליקה (3₪)' },
    { value: 'no_clearing_fee', label: 'ללא דמי סליקה' }
]

const extraChargeOptions = [
    { value: 'clearing_fee', label: 'דמי סליקה (3₪)' },
    { value: 'no_clearing_fee', label: 'ללא דמי סליקה' }
]

const debitCreditTypeOptions = [
    { value: 'immediate_and_recurring', label: 'חיוב מיידי כולל הו״ק' },
    { value: 'immediate_only', label: 'חיוב מיידי בלבד' },
    { value: 'recurring_only', label: 'הו״ק בלבד' }
]

// ── helpers ──────────────────────────────────────────────────
const formatCardNumber = val => {
    const digits = (val || '').replace(/\D/g, '').slice(0, 19)
    return digits.replace(/(.{4})/g, '$1 ').trim()
}

const parseCardNumber = val => (val || '').replace(/\s/g, '')

const detectCardNetwork = (digits: string): string | null => {
    const match = cardNetworks.find(n => n.pattern.test(digits))
    return match ? match.name : null
}

const rightLabel = { labelCol: { style: { textAlign: 'right' as 'right' } } }

// ── sub-components ───────────────────────────────────────────
const SubSection = ({ title, children }) => (
    <div style={{ marginTop: 8, marginRight: 16 }}>
        <div style={{ fontWeight: 'bold', textAlign: 'right', marginBottom: 4 }}>{title}</div>
        {children}
    </div>
)

const BankFields = ({ getFieldDecorator, getFieldValue, onDebitTypeChange, onBankSubRadioChange }) => {
    const bankFields = [
        { name: 'branch_code', label: 'קוד סניף', message: 'יש להזין קוד סניף', maxLength: 3 },
        { name: 'account_number', label: 'מספר חשבון', message: 'יש להזין מספר חשבון', maxLength: 10 }
    ]

    const debitType = getFieldValue('debit_type')
    const isImmediateAndRecurring = debitType === 'immediate_and_recurring'
    const isRecurringOnly = debitType === 'recurring_only'

    return (
        <>
            <FormItem label="קוד בנק" {...rightLabel}>
                {getFieldDecorator('bank_code', {
                    rules: [{ required: true, message: 'יש לבחור בנק' }]
                })(
                    <Select
                        className={selectInput}
                        placeholder="בחר בנק"
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                            const text = String(option.props.children || '')
                            return text.includes(input) || String(option.props.value || '').includes(input)
                        }}
                        dropdownStyle={{ direction: 'rtl', textAlign: 'right' }}
                    >
                        {israelRetailBanks.map(bank => (
                            <Select.Option key={bank.key} value={String(bank.key)}>
                                {`${bank.key} - ${bank.heb_name}`}
                            </Select.Option>
                        ))}
                    </Select>
                )}
            </FormItem>

            {bankFields.map(({ name, label, message, maxLength }) => (
                <FormItem key={name} label={label} {...rightLabel}>
                    {getFieldDecorator(name, {
                        rules: [{ required: true, message }],
                        getValueFromEvent: e => (e.target.value || '').replace(/\D/g, '')
                    })(<Input className={textInput} maxLength={maxLength} placeholder={'0'.repeat(maxLength)} />)}
                </FormItem>
            ))}

            <FormItem label="פרטים נוספים" {...rightLabel}>
                {getFieldDecorator('remarks')(
                    <TextArea rows={1} placeholder="הוסף פרטים נוספים" style={{ resize: 'vertical' }} />
                )}
            </FormItem>

            <FormItem {...rightLabel}>
                {getFieldDecorator('debit_type', { initialValue: 'immediate_and_recurring' })(
                    <Radio.Group onChange={onDebitTypeChange}>
                        {debitTypeOptions.map(opt => (
                            <Radio key={opt.value} value={opt.value} style={{ display: 'block', textAlign: 'right' }}>
                                {opt.label}
                            </Radio>
                        ))}
                    </Radio.Group>
                )}
            </FormItem>

            {isImmediateAndRecurring && (
                <>
                    <SubSection title="חודש ראשון:">
                        <FormItem style={{ marginBottom: 0 }}>
                            {getFieldDecorator('first_month_charge', { initialValue: 'setup_fee' })(
                                <Radio.Group
                                    onChange={e => onBankSubRadioChange({ first_month_charge: e.target.value })}
                                >
                                    {firstMonthOptions.map(opt => (
                                        <Radio
                                            key={opt.value}
                                            value={opt.value}
                                            style={{ display: 'block', textAlign: 'right' }}
                                        >
                                            {opt.label}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            )}
                        </FormItem>
                    </SubSection>

                    <SubSection title="החל מחודש שני:">
                        <FormItem style={{ marginBottom: 0 }}>
                            {getFieldDecorator('recurring_charge', { initialValue: 'clearing_fee' })(
                                <Radio.Group onChange={e => onBankSubRadioChange({ recurring_charge: e.target.value })}>
                                    {recurringChargeOptions.map(opt => (
                                        <Radio
                                            key={opt.value}
                                            value={opt.value}
                                            style={{ display: 'block', textAlign: 'right' }}
                                        >
                                            {opt.label}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            )}
                        </FormItem>
                    </SubSection>
                </>
            )}

            {isRecurringOnly && (
                <SubSection title=":דמי חיוב נוספים">
                    <FormItem style={{ marginBottom: 0 }}>
                        {getFieldDecorator('extra_charge', { initialValue: 'clearing_fee' })(
                            <Radio.Group onChange={e => onBankSubRadioChange({ extra_charge: e.target.value })}>
                                {extraChargeOptions.map(opt => (
                                    <Radio
                                        key={opt.value}
                                        value={opt.value}
                                        style={{ display: 'block', textAlign: 'right' }}
                                    >
                                        {opt.label}
                                    </Radio>
                                ))}
                            </Radio.Group>
                        )}
                    </FormItem>
                </SubSection>
            )}
        </>
    )
}

const CreditFields = ({ getFieldDecorator, onDebitTypeChange, getFieldValue, setFieldValue, roles }) => {
    const hasImmediateAccess = roles.includes('admin') || roles.includes('money')
    const filteredDebitCreditTypeOptions = hasImmediateAccess
        ? debitCreditTypeOptions
        : debitCreditTypeOptions.filter(opt => opt.value !== 'immediate_only')
    const nameField = getFieldValue('client_full_name')
    const idField = getFieldValue('id')
    const cardNetwork = detectCardNetwork(parseCardNumber(getFieldValue('card_number') || ''))
    const cardNumberLabel = cardNetwork ? `מספר כרטיס - ${cardNetwork}` : 'מספר כרטיס'

    return (
        <>
            <FormItem label={cardNumberLabel} {...rightLabel}>
                {getFieldDecorator('card_number', {
                    rules: [
                        { required: true, message: 'יש להזין מספר כרטיס' },
                        {
                            validator: (_rule, value, callback) => {
                                const digits = parseCardNumber(value)
                                if (!digits) {
                                    callback()
                                    return
                                }
                                if (digits.length < 12 || digits.length > 19) {
                                    callback('מספר כרטיס לא תקין')
                                    return
                                }
                                if (!luhnCheck(digits)) {
                                    callback('מספר כרטיס לא תקין')
                                    return
                                }
                                if (!detectCardNetwork(digits)) {
                                    callback('סוג כרטיס לא מזוהה')
                                    return
                                }
                                callback()
                            }
                        }
                    ],
                    getValueFromEvent: e => formatCardNumber(e.target.value)
                })(
                    <Input
                        maxLength={23}
                        placeholder="0000 0000 0000 0000"
                        style={{ width: '100%', direction: 'ltr', letterSpacing: '2px' }}
                    />
                )}
            </FormItem>

            <FormItem label="חודש תפוגה" {...rightLabel} style={{ width: '50%', display: 'inline-block' }}>
                {getFieldDecorator('expire_month', { initialValue: currentMonth })(
                    <Select style={{ width: '100%' }}>
                        {expireMonthOptions.map(m => (
                            <Select.Option key={m.value} value={m.value}>
                                {m.label}
                            </Select.Option>
                        ))}
                    </Select>
                )}
            </FormItem>

            <FormItem label="שנת תפוגה" {...rightLabel} style={{ width: '50%', display: 'inline-block' }}>
                {getFieldDecorator('expire_year', { initialValue: currentYear })(
                    <Select style={{ width: '100%' }}>
                        {expireYearOptions.map(y => (
                            <Select.Option key={y.value} value={y.value}>
                                {y.label}
                            </Select.Option>
                        ))}
                    </Select>
                )}
            </FormItem>

            <FormItem label="קוד אבטחה (CVV)" {...rightLabel}>
                {getFieldDecorator('cvv_code', {
                    rules: [
                        { required: true, message: 'יש להזין קוד CVV' },
                        { pattern: /^[0-9]{3,4}$/, message: 'קוד CVV חייב להכיל 3 או 4 ספרות' }
                    ],
                    getValueFromEvent: e => (e.target.value || '').replace(/\D/g, '')
                })(<Input maxLength={4} className={textInput} placeholder={'000'} />)}
            </FormItem>

            <FormItem label="שם בעל הכרטיס" {...rightLabel} style={{ position: 'relative' }}>
                <div
                    style={{ display: nameField ? 'flex' : 'none' }}
                    className={addIcon}
                    onClick={() => setFieldValue('card_holder_name', nameField)}
                >
                    <Tooltip placement="topRight" title="שימוש בפרטי לקוח">
                        <Icon type="plus" style={{ cursor: 'pointer', top: 'unset', color: '#52c41a' }} />
                    </Tooltip>
                </div>
                {getFieldDecorator('card_holder_name', {
                    rules: [
                        { required: true, message: 'יש להזין שם בעל הכרטיס' },
                        { pattern: /^[^\d]*$/, message: 'שם לא יכול להכיל מספרים' }
                    ],
                    getValueFromEvent: e => (e.target.value || '').replace(/\d/g, ''),
                    initialValue: ''
                })(<Input className={textInput} style={{ zIndex: 1 }} placeholder="הזן שם בעל הכרטיס" />)}
            </FormItem>

            <FormItem label="תעודת זהות בעל הכרטיס" {...rightLabel}>
                <div
                    style={{ display: idField ? 'flex' : 'none' }}
                    className={addIcon}
                    onClick={() => setFieldValue('card_holder_id', idField)}
                >
                    <Tooltip placement="topRight" title="שימוש בפרטי לקוח">
                        <Icon type="plus" style={{ cursor: 'pointer', top: 'unset', color: '#52c41a' }} />
                    </Tooltip>
                </div>
                {getFieldDecorator('card_holder_id', {
                    rules: [
                        { required: true, message: 'יש להזין תעודת זהות' },
                        { pattern: /^[0-9]{9}$/, message: 'יש להזין 9 ספרות' },
                        {
                            validator: (_, value, callback) => {
                                if (!value || value.length !== 9) return callback()
                                const padded = value.padStart(9, '0')
                                const sum = padded.split('').reduce((acc, digit, i) => {
                                    let n = Number(digit) * ((i % 2) + 1)
                                    if (n > 9) n -= 9
                                    return acc + n
                                }, 0)
                                callback(sum % 10 === 0 ? undefined : 'תעודת זהות לא תקינה')
                            }
                        }
                    ],
                    getValueFromEvent: e => (e.target.value || '').replace(/\D/g, '')
                })(<Input className={textInput} maxLength={9} placeholder="הזן תעודת זהות" />)}
            </FormItem>

            <FormItem label="פרטים נוספים" {...rightLabel}>
                {getFieldDecorator('remarks')(
                    <TextArea rows={1} placeholder="הוסף פרטים נוספים" style={{ resize: 'vertical' }} />
                )}
            </FormItem>

            <FormItem {...rightLabel}>
                {getFieldDecorator('debit_type', { initialValue: 'immediate_and_recurring' })(
                    <Radio.Group onChange={onDebitTypeChange}>
                        {filteredDebitCreditTypeOptions.map(opt => (
                            <Radio key={opt.value} value={opt.value}>
                                {opt.label}
                            </Radio>
                        ))}
                    </Radio.Group>
                )}
            </FormItem>
        </>
    )
}

// ── main component ───────────────────────────────────────────
const PaymentMethod = ({
    terminalType,
    onDebitTypeChange,
    onBankSubRadioChange,
    roles,
    getFieldDecorator,
    getFieldValue,
    setFieldValue
}: {
    terminalType: string
    onDebitTypeChange: (e: any) => void
    onBankSubRadioChange: (overrides: Record<string, string>) => void
    roles: string[]
} & FormContextValue) => {
    const isBank = terminalType === 'mediwhobk'

    return (
        <>
            <div className={columnTitle}>פרטי אמצעי תשלום</div>

            <FormItem label="מסוף טוקנים לחיובים חוזרים" {...rightLabel}>
                <Input className={textInput} value={terminalType} disabled />
            </FormItem>

            {isBank ? (
                <BankFields
                    getFieldDecorator={getFieldDecorator}
                    getFieldValue={getFieldValue}
                    onDebitTypeChange={onDebitTypeChange}
                    onBankSubRadioChange={onBankSubRadioChange}
                />
            ) : (
                <CreditFields
                    getFieldDecorator={getFieldDecorator}
                    onDebitTypeChange={onDebitTypeChange}
                    getFieldValue={getFieldValue}
                    setFieldValue={setFieldValue}
                    roles={roles}
                />
            )}
        </>
    )
}

export default withFormContext(PaymentMethod)
